"use client";

import { Button } from "@/components/ui/button-pro";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { Message, useChat } from "@ai-sdk/react";
// import { Message } from "ai/react";
import {
  chat_email_verified,
  chatbot_user_email,
  isValidEmail,
  myPhotoPath,
} from "@/lib/lib";
import { AnimatePresence, motion } from "framer-motion";
import {
  BotIcon,
  Maximize2,
  MessageCircle,
  Send,
  UserIcon,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { FaRobot } from "react-icons/fa6";
import ReactMarkdown from "react-markdown";
import { toast } from "sonner";
import { AISocialLinks } from "../common/social-links";
import { EmailVerificationFlow } from "../contact/email-verification";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { ScrollArea, ScrollBar } from "../ui/scroll-area";
import { Separator } from "../ui/separator";
import { ChatbotFullscreenModal } from "./chatbot-fullscreen-modal";

const initMessages: Message[] = [
  {
    id: "welcome-message",
    role: "assistant",
    content: "Hi there! I'm your AI assistant. How can I help you today?",
  },
];

export function ChatbotWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const chatWidgetRef = useRef<HTMLDivElement>(null);

  const [userEmail, setUserEmail] = useState<string | null>(chatbot_user_email);
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [isOnline, setIsOnline] = useState(false);

  const {
    messages,
    input,
    handleInputChange,
    handleSubmit: handleChatSubmit,
    status,
    setMessages,
  } = useChat({
    api: "/api/chat",
    initialMessages: initMessages,
    // Send only custom fields; the AI SDK includes `messages` automatically
    body: {
      email: userEmail,
    },
    onError: (error) => {
      console.error("Chat error:", error);
      toast.error("Failed to send message. Please try again.");
    },
  });

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const checkOnlineStatus = () => {
      const now = new Date();
      const tunisiaTime = new Date(
        now.toLocaleString("en-US", { timeZone: "Africa/Tunis" })
      );
      const hour = tunisiaTime.getHours();
      setIsOnline(hour >= 8 && hour < 20);
    };

    checkOnlineStatus();
    const interval = setInterval(checkOnlineStatus, 60000); // Check every minute
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Check if user has valid session
    const checkSession = async () => {
      // try {
      if (!isValidEmail(userEmail)) return;

      // const resTodo = await fetch("/api/verfiy-chat-session");

      const response = await fetch("/api/check-user", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: chatbot_user_email }),
      });

      if (response.ok) {
        const data = await response.json();

        if (data?.exists && data.user) {
          setEmailSubmitted(data.exists);
          setUserEmail(data.user.email);
        }
      } else {
        setUserEmail(null);
      }
    };

    if (isOpen) {
      checkSession();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  // Handle double-click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        chatWidgetRef.current &&
        !chatWidgetRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);

        // double click
        // setClickCount(prev => prev + 1);

        // // Reset click count after 500ms if no second click
        // setTimeout(() => {
        //   setClickCount(0);
        // }, 500);
      }
    };

    // Close on double click outside
    // if (clickCount >= 2) {
    //   setIsOpen(false);
    //   setClickCount(0);
    // }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Handle Escape key to close
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        setIsOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscapeKey);
    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [isOpen]);

  // Check if email was previously submitted (using sessionStorage for demo)
  // useEffect(() => {
  //   // if (userEmail === "" && last_chatbot_user_email) {
  //   if (!userEmail && isValidEmail(chatbot_user_email)) {
  //     setUserEmail(chatbot_user_email);
  //     // setEmailSubmitted(true);
  //   }
  // }, [userEmail, chatbot_user_email]);
  // }, []);

  const handleEmailVerified = async (email: string) => {
    if (!isValidEmail(userEmail)) {
      toast.warning("Please enter a valid address");
      return;
    }

    try {
      // const data = await saveUserEmail(email);
      const response = await fetch("/api/save-user-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (data.success) {
        setUserEmail(email); // IMPORTANT
        setEmailSubmitted(true);
        toast.success("Email verified successfully!");

        // GOOD
        sessionStorage.removeItem("last_chatbot_user_email");
        sessionStorage.setItem("chatbot_user_email", email);

        const welcomeMessage: Message = {
          id: Date.now().toString(),
          content: `Hi there! Thanks for verifying your email. I'm here to help answering your questions`,
          role: "user",
          createdAt: new Date(),
        };
        setMessages([welcomeMessage, ...messages]);
      } else {
        toast.error("Failed to verify email. Please try again.");
        setEmailSubmitted(false);
      }
    } catch (error) {
      console.error("Error saving user:", error);
      toast.error("Something went wrong. Please try again.");
    }
  };

  // const handleSwitchEmail = () => {
  //   setEmailSubmitted(false);
  //   sessionStorage.removeItem("chatbot_user_email");
  //   if (isValidEmail(userEmail))
  //     sessionStorage.setItem("last_chatbot_user_email", userEmail!);
  // };

  // Floating Chat Button
  if (!isOpen) {
    return (
      <div className="fixed overflow-hidden bottom-2 right-2 z-40">
        <Button
          onClick={() => setIsOpen(true)}
          className="group relative bg-green-500 hover:bg-green-600 text-white m-2 p-2 rounded-full shadow-2xl transition-all duration-300 hover:scale-110"
        >
          <MessageCircle size={34} />

          {/* Pulse animation */}
          <div className="absolute inset-0 rounded-full bg-green-500 hover:animate-ping opacity-20" />

          {/* Notification dot */}
          <div className="absolute top-0 -right-1 w-3 h-3 bg-red-500 rounded-full animate-bounce"></div>
        </Button>
      </div>
    );
  }

  return (
    <>
      {/* Animated overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            key="chat-overlay"
            className="fixed inset-0 z-40 bg-black/30 backdrop-blur-[2px]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => setIsOpen(false)}
          />
        )}
      </AnimatePresence>

      <div className="relative z-50">
        {/* Chatbot toggle button */}
        {/* <Button
          size={"icon"}
          onClick={handleToggle}
          className={cn(
            "fixed top-3 right-3 p-4 z-50 rounded-full shadow-lg bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 dark:from-blue-600 dark:to-slate-800 transition-all duration-300 text-black dark:text-white hover:scale-105",
            isOpen && "rotate-90"
          )}
          aria-label="Toggle chatbot"
          aria-expanded={isOpen}
          aria-controls="chatbot-panel"
        >
          <X className="h-6 w-6" />
        </Button> */}

        {/* Chat Content */}
        <AnimatePresence>
          {
            <motion.div
              id="chatbot-panel"
              key="chat-panel"
              ref={chatWidgetRef}
              className={cn(
                "fixed bottom-2 right-2 max-w-[450px] origin-bottom-right",
                "drop-shadow-2xl"
              )}
              initial={{ opacity: 0, y: 16, scale: 0.96 }}
              animate={{
                opacity: isOpen ? 1 : 0,
                y: isOpen ? 0 : 16,
                scale: isOpen ? 1 : 0.96,
              }}
              exit={{ opacity: 0, y: 16, scale: 0.96 }}
              transition={{
                type: "spring",
                stiffness: 360,
                damping: 28,
                mass: 0.8,
              }}
              style={{ pointerEvents: isOpen ? "auto" : "none" }}
            >
              {!emailSubmitted && chat_email_verified !== "true" ? (
                // Email Input Screen
                <div className="h-full p-6 flex flex-col justify-center items-center dark:bg-gray-800 bg-slate-100 rounded-lg shadow-xl border animate-in slide-in-from-bottom-4 duration-300">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleToggle}
                    className="absolute top-2 right-2 hover:bg-white/20 transition-colors"
                  >
                    <X className="h-4 w-4 text-slate-900 hover:rotate-90 dark:text-white" />
                  </Button>

                  <div className="text-center mb-6">
                    {/* <div className="flex items-center justify-center"> */}
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <MessageCircle className="text-green-500" size={24} />
                    </div>
                    <h4 className="font-semibold dark:text-white text-gray-800 mb-2">
                      Let's start chatting!
                    </h4>
                    {/* </div> */}
                    <p className="text-sm text-gray-600 dark:text-white/90">
                      Verify your email to begin the conversation.
                    </p>
                  </div>

                  <EmailVerificationFlow
                    onVerified={handleEmailVerified}
                    // emailProp={userEmail}
                    // className="w-full"
                  />

                  <p className="text-xs text-gray-500 text-center mt-4">
                    Your email helps me provide personalized responses and
                    follow up on our conversation.
                  </p>
                </div>
              ) : (
                // Chat interface
                <Card className="gap-1 shadow-xl border border-purple-500/20 p-0 bg-white/95 dark:bg-gray-900/95 backdrop-blur-lg flex flex-col rounded-lg animate-in slide-in-from-bottom-4 duration-300">
                  <CardHeader className="bg-gradient-to-r rounded-t-lg dark:from-purple-500 dark:to-blue-600 to-slate-200 dark:text-white py-1 px-3 flex flex-row items-center justify-between flex-shrink-0">
                    <div className="flex items-center space-x-2">
                      <div className="relative">
                        <Avatar>
                          <AvatarImage
                            src={myPhotoPath}
                            className="rounded-full"
                            sizes="(min-width: 1024px) 100px, (min-width: 768px) 80px, 60px"
                          />
                          <AvatarFallback>
                            <FaRobot className="h-5 w-5" />
                          </AvatarFallback>
                        </Avatar>
                        <div
                          className={cn(
                            "absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white",
                            isOnline ? "bg-green-500" : "bg-gray-400"
                            // status === "streaming"
                            //   ? "bg-yellow-500"
                            //   : status === "error"
                            //     ? "bg-red-500"
                            //     : "bg-green-500"
                          )}
                        />
                      </div>
                      <div>
                        <p className="font-medium lg:text-lg text-base">
                          Hamza AI Chat
                        </p>
                        <div className="flex items-center gap-1">
                          {/* <div
                            className={`w-2 h-2 rounded-full ${isOnline ? "bg-green-500" : "bg-gray-400"}`}
                          /> */}
                          <span className="text-xs text-muted-foreground">
                            {isOnline ? "Online" : "Offline"}
                          </span>
                        </div>
                        {/* <p className="text-xs opacity-75">
                          {status === "streaming"
                            ? "Typing..."
                            : status === "error"
                              ? "Connection error"
                              : "Online"}
                        </p> */}
                      </div>
                    </div>

                    {/* Reset option */}
                    <div className="flex items-center space-x-1">
                      {/* <Button
                        onClick={handleSwitchEmail}
                        variant="outline"
                        size="sm"
                        className="text-xs text-gray-100 hover:text-gray-300 px-2 py-1 bg-transaprent border border-transparent"
                      >
                        <TbSwitchHorizontal className="h-3 w-3" />
                        <span>Email</span>
                      </Button> */}

                      <Button
                        onClick={() => setIsFullscreen(!isFullscreen)}
                        variant="ghost"
                        size="icon"
                        className="dark:text-white text-slate-900 hover:bg-white/20 p-1"
                      >
                        <Maximize2 className="h-4 w-4" />
                      </Button>

                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={handleToggle}
                        className="dark:text-white z-50 text-slate-900 hover:bg-white/20 p-1"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardHeader>

                  <Separator className="bg-purple-800 dark:bg-white mx-auto max-w-xl" />

                  <CardContent className="p-0 w-full">
                    {/* {Array.isArray(messages) && messages.length === 0 && ( */}
                    <div className="text-center text-muted-foreground text-sm py-8">
                      👋 Hi {userEmail}! Ask me about Hamza Dev or anything
                      related to his work.
                    </div>
                    {/* )} */}

                    {Array.isArray(messages) && messages.length > 0 && (
                      <ScrollArea className="w-full mt-1 border-y bg-gray-200 dark:bg-gray-400 overflow-y-auto max-h-[500px] px-3 py-1">
                        <div className="flex flex-col gap-2 py-2">
                          {messages.map((message) => (
                            // <div
                            //   className={cn(
                            //     "flex flex-col space-y-2 text-sm  max-w-[90%] rounded-lg p-2",
                            //       ? "ml-auto dark:bg-gray-800 dark:text-white text-black bg-gray-200"
                            //       : "bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                            //   )}
                            // >
                            <div
                              key={message.id}
                              className={cn(
                                "flex gap-1 w-full rounded-lg p-2 justify-between",
                                message.role === "user"
                                  ? "ml-auto justify-self-end flex-row-reverse items-center"
                                  : "mr-auto justify-self-start mt-1 items-start",
                                message.role === "user"
                                  ? "ml-auto dark:bg-purple-800 dark:text-white text-black bg-purple-200"
                                  : "bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                              )}
                            >
                              {message.role === "user" ? (
                                <UserIcon className="h-6 w-6" />
                              ) : (
                                <BotIcon className="h-6 w-6" />
                              )}

                              <ScrollArea className="overflow-y-auto max-w-[80%] flex-1 max-h-[150px] py-1 pr-2">
                                <ReactMarkdown
                                  components={{
                                    a: ({ node, ...props }) => (
                                      <a
                                        {...props}
                                        className="text-blue-500 hover:underline"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      />
                                    ),
                                    p: ({ node, ...props }) => (
                                      <p
                                        {...props}
                                        className="leading-relaxed text-sm"
                                      />
                                    ),
                                  }}
                                >
                                  {message.content}
                                </ReactMarkdown>
                                <ScrollBar orientation="vertical" />
                              </ScrollArea>
                            </div>
                          ))}
                          <div ref={messagesEndRef} />
                        </div>
                        <ScrollBar
                          orientation="vertical"
                          className="h-[100px] overflow-y-hidden"
                        />
                      </ScrollArea>
                    )}
                  </CardContent>

                  {/* flex-shrink-0 */}
                  <CardFooter className="p-0">
                    <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800">
                      <div className="pt-2 flex justify-center shadow-md shadow-purple-600 m-1 border">
                        <AISocialLinks
                          messages={messages}
                          setMessages={setMessages}
                        />
                      </div>

                      <form onSubmit={handleChatSubmit} className="p-3">
                        <div className="flex gap-2 w-full items-center">
                          <Input
                            ref={inputRef}
                            value={input}
                            onChange={handleInputChange}
                            placeholder="Type your message..."
                            disabled={status === "streaming"}
                            className="flex-1 bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          />
                          <Button
                            size="icon"
                            disabled={status === "streaming" || !input.trim()}
                            className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white transition-all duration-200 hover:scale-105"
                            type="submit"
                          >
                            <Send
                              className={cn(
                                "h-4 w-4 transition-transform",
                                status === "streaming" && "animate-pulse"
                              )}
                            />
                            <span className="sr-only">Send</span>
                          </Button>
                        </div>
                      </form>
                    </div>
                  </CardFooter>
                </Card>
              )}
            </motion.div>
          }
        </AnimatePresence>
      </div>

      {/* Tooltip for double-click hint */}
      {/* {isOpen && (
        <div className="fixed bottom-[520px] right-5 hidden md:block">
          <div className="bg-gray-800 text-white text-xs px-2 py-1 rounded shadow-lg opacity-75 animate-in fade-in duration-500">
            Double-click outside to close • Press ESC
          </div>
        </div>
      )} */}

      {/* Fullscreen Modal */}
      <ChatbotFullscreenModal
        isOpen={isFullscreen}
        onClose={() => setIsFullscreen(false)}
        messages={messages}
        setMessages={setMessages}
        input={input}
        handleInputChange={handleInputChange}
        handleSubmit={handleChatSubmit}
        status={status}
        userEmail={userEmail}
        isOnline={isOnline}
      />
    </>
  );
}
